"use client";

import { get_captcha_config_query } from "@core/services/settings";

// Cap (https://trycap.dev) tokens for the auth mutations.
// A token works for ONE server-side check; the ones the server never checks (hasUser, SMSLoginVerifyToken)
// may be sent without spending it.
//
// Two ways a token comes to exist, and both end up in the same store:
//  - a <CapCaptcha /> on the page: the visitor ticks it (or the hook solves it through the same widget when
//    they have not), and the widget goes back to unchecked once its token is spent;
//  - no widget on the page: a hidden one solves the proof-of-work in the background.

const TOKEN_TTL_MS = 4 * 60 * 1000; // well inside what Cap accepts

let config_promise = null;
let cached = null; // { token, expires_at }
let pending = null; // the solve in flight
const widgets = []; // <cap-widget> elements on the page, the last one is the one in use

// { api_endpoint, places } of the site's CAP service, fetched once; null when it has none
export const get_cap_config = () => {
  if (!config_promise) {
    config_promise = get_captcha_config_query()
      .then((result) => result?.data?.api_endpoint ? result.data : null)
      .catch(() => null)
      .then((config) => {
        if (!config) config_promise = null; // ask again next time
        return config;
      });
  }
  return config_promise;
};

export const get_cap_endpoint = async () => (await get_cap_config())?.api_endpoint || null;

// The site switches the captcha off per place ("login", "register", "change-mobile", "change-email") in its CAP
// service. `place` is a name or a list of names (one on is enough); without it, any place on counts. When the
// server does not list places at all, everything counts as on.
export const cap_place_is_on = (places, place) => {
  if (!Array.isArray(places)) return true;
  const wanted = [].concat(place ?? []);
  return wanted.length ? wanted.some((p) => places.includes(p)) : places.length > 0;
};

const place_is_on = async (place) => cap_place_is_on((await get_cap_config())?.places, place);

// The checkbox is not part of the form: it floats over the page while a token is being solved, next to its anchor
// (the spot where <CapCaptcha /> sits), and goes away once it is done.
const FLOAT_MARGIN = 8;

const float_place = (widget, anchor) => {
  const width = widget.offsetWidth;
  const height = widget.offsetHeight;
  let left;
  let top;
  if (anchor && anchor.getClientRects().length > 0) {
    const rect = anchor.getBoundingClientRect();
    left = rect.left + rect.width / 2 - width / 2;
    top = rect.top - height - FLOAT_MARGIN; // above the anchor, below it when there is no room
    if (top < FLOAT_MARGIN) top = rect.bottom + FLOAT_MARGIN;
    top = Math.min(top, window.innerHeight - height - FLOAT_MARGIN);
  } else {
    // the anchor is on a screen that is not showing: centre it
    left = (window.innerWidth - width) / 2;
    top = (window.innerHeight - height) / 2;
  }
  widget.style.left = `${Math.max(FLOAT_MARGIN, Math.min(left, window.innerWidth - width - FLOAT_MARGIN))}px`;
  widget.style.top = `${Math.max(FLOAT_MARGIN, top)}px`;
};

export const float_in = (widget, anchor) => {
  clearTimeout(widget._float_timer);
  Object.assign(widget.style, {
    display: "block",
    position: "fixed",
    zIndex: "99999",
    opacity: "0",
    transform: "scale(0.98)",
    transition: "opacity 0.15s, transform 0.2s",
    boxShadow: "rgba(0, 0, 0, 0.12) 0px 6px 24px 0px",
    borderRadius: "14px",
  });
  float_place(widget, anchor);
  setTimeout(() => {
    widget.style.opacity = "1";
    widget.style.transform = "scale(1)";
  }, 10);
};

// stays a moment after the solve so the visitor sees the check turn green
export const float_out = (widget, delay = 600) => {
  clearTimeout(widget._float_timer);
  widget._float_timer = setTimeout(() => {
    widget.style.opacity = "0";
    widget.style.transform = "scale(0.98)";
    widget._float_timer = setTimeout(() => {
      widget.style.display = "none";
    }, 200);
  }, delay);
};

export const load_cap_widget = () => import("@cap.js/widget");

export const store_cap_token = (token) => {
  cached = token ? { token, expires_at: Date.now() + TOKEN_TTL_MS } : null;
};

export const clear_cap_token = () => {
  cached = null;
};

// the <CapCaptcha /> component hands its element over while it is mounted; returns the way to take it back
export const register_cap_widget = (element) => {
  widgets.push(element);
  return () => {
    const index = widgets.indexOf(element);
    if (index >= 0) widgets.splice(index, 1);
  };
};

// the widget floats in next to its component's anchor while it solves (the component hands the anchor over as
// widget._cap_anchor)
const solve_with_widget = async (widget) => {
  float_in(widget, widget._cap_anchor?.current);
  try {
    const { token } = await widget.solve();
    return token || null;
  } finally {
    float_out(widget);
  }
};

const solve_hidden = async () => {
  const api_endpoint = await get_cap_endpoint();
  if (!api_endpoint) return null;

  await load_cap_widget();
  const cap = new window.Cap({ apiEndpoint: api_endpoint });
  try {
    const { token } = await cap.solve();
    return token || null;
  } finally {
    cap.widget?.remove?.();
  }
};

// the widget on the page does the solving when there is one; if it fails (it can end up in its error state),
// the hidden one takes over so the request still goes out with a token
const solve_any = async (widget) => {
  if (widget) {
    try {
      const token = await solve_with_widget(widget);
      if (token) return token;
    } catch {
      // fall through to the hidden solve
    }
  }
  return solve_hidden();
};

const take = (consume) => {
  if (!cached || cached.expires_at <= Date.now()) return undefined;
  const token = cached.token;
  if (consume) {
    cached = null;
    widgets[widgets.length - 1]?.reset?.(); // its token is spent, the visitor gets a fresh checkbox
  }
  return token;
};

/**
 * place: where the token is asked ("login", "register", "change-mobile", "change-email", or a list of them); a place
 * the site switched off resolves to undefined at once, without solving anything.
 * consume: the token is going to a mutation that checks it, so it is not handed out again.
 * solve_if_missing: false sends only a token that is already solved, never starts a solve for it.
 * Resolves to undefined when there is no token (service off, Cap unreachable): the request goes without one.
 */
export const get_cap_token = async ({ place, consume = true, solve_if_missing = true } = {}) => {
  if (place && !(await place_is_on(place))) return undefined;

  const ready = take(consume);
  if (ready || !solve_if_missing) return ready;

  if (!pending) {
    const widget = widgets[widgets.length - 1];
    pending = solve_any(widget)
      .then((token) => store_cap_token(token))
      .catch(() => {})
      .finally(() => {
        pending = null;
      });
  }
  await pending;
  return take(consume);
};
